/**
 * HUDBackground — animated canvas that renders behind all panels.
 * Draws a hex grid + floating particles that react to the current theme.
 * Uses requestAnimationFrame for smooth 60fps rendering.
 */

import { useEffect, useRef, memo } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';

// ─── Hex grid helpers ─────────────────────────────────────────────────────────

function hexCorner(cx: number, cy: number, size: number, i: number): [number, number] {
  const angle = (Math.PI / 3) * i - Math.PI / 6;
  return [cx + size * Math.cos(angle), cy + size * Math.sin(angle)];
}

// ─── Particle ────────────────────────────────────────────────────────────────

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  size: number;
}

function makeParticle(W: number, H: number): Particle {
  return {
    x:       Math.random() * W,
    y:       Math.random() * H,
    vx:      (Math.random() - 0.5) * 0.3,
    vy:      (Math.random() - 0.5) * 0.3,
    life:    Math.random() * 200,
    maxLife: 150 + Math.random() * 200,
    size:    0.5 + Math.random() * 1.5,
  };
}

// ─── Theme color resolver ─────────────────────────────────────────────────────

function getThemeColor(): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--color-arc').trim() || '#00BFFF';
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const val   = parseInt(clean, 16);
  return [(val >> 16) & 255, (val >> 8) & 255, val & 255];
}

// ─── Canvas Component ─────────────────────────────────────────────────────────

export const HUDBackground = memo(function HUDBackground() {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const frameRef   = useRef<number>(0);
  const particles  = useRef<Particle[]>([]);
  const theme      = useTerminalStore(s => s.theme);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W = 0, H = 0;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      // Repopulate particles on resize
      particles.current = Array.from({ length: 60 }, () => makeParticle(W, H));
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const HEX_SIZE  = 36;
    const HEX_W     = HEX_SIZE * Math.sqrt(3);
    const HEX_H     = HEX_SIZE * 2;

    let frame = 0;

    const draw = () => {
      frameRef.current = requestAnimationFrame(draw);
      frame++;

      const arcColor   = getThemeColor();
      const [r, g, b]  = hexToRgb(arcColor);
      const isLight    = theme === 'light';

      ctx.clearRect(0, 0, W, H);

      // ── Background gradient ─────────────────────────────────────────────
      const bg = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-bg').trim() || '#020a14';
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Apply CSS bg-pattern via inline style on body — skip redrawing here

      // ── Hex grid ────────────────────────────────────────────────────────
      const hexAlpha = isLight ? 0.06 : 0.04;
      ctx.strokeStyle = `rgba(${r},${g},${b},${hexAlpha})`;
      ctx.lineWidth   = 0.5;

      const cols = Math.ceil(W / HEX_W) + 2;
      const rows = Math.ceil(H / (HEX_H * 0.75)) + 2;

      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const cx = col * HEX_W + (row % 2 === 0 ? 0 : HEX_W / 2);
          const cy = row * HEX_H * 0.75;

          // Subtle pulse — some hexes glow brighter on a slow sine
          const pulse = Math.sin(frame * 0.003 + col * 0.4 + row * 0.3);
          const alpha = isLight
            ? 0.04 + pulse * 0.02
            : 0.02 + pulse * 0.02;

          ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const [hx, hy] = hexCorner(cx, cy, HEX_SIZE - 1, i);
            i === 0 ? ctx.moveTo(hx, hy) : ctx.lineTo(hx, hy);
          }
          ctx.closePath();
          ctx.stroke();
        }
      }

      // ── Scan lines (horizontal, very faint) ─────────────────────────────
      if (!isLight) {
        for (let y = 0; y < H; y += 4) {
          ctx.fillStyle = `rgba(0,0,0,0.025)`;
          ctx.fillRect(0, y, W, 1);
        }
      }

      // ── Particles ───────────────────────────────────────────────────────
      for (let i = 0; i < particles.current.length; i++) {
        const p = particles.current[i];
        if (!p) continue;

        p.x    += p.vx;
        p.y    += p.vy;
        p.life += 1;

        if (p.life > p.maxLife || p.x < 0 || p.x > W || p.y < 0 || p.y > H) {
          particles.current[i] = makeParticle(W, H);
          continue;
        }

        const t      = p.life / p.maxLife;
        const alpha  = (isLight ? 0.3 : 0.5) * Math.sin(t * Math.PI);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fill();
      }

      // ── Corner accent lines ──────────────────────────────────────────────
      const lineAlpha = isLight ? 0.15 : 0.12;
      const lineColor = `rgba(${r},${g},${b},${lineAlpha})`;
      const lineLen   = 80;

      ctx.strokeStyle = lineColor;
      ctx.lineWidth   = 1;

      // Top-left
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(lineLen, 0); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, lineLen); ctx.stroke();
      // Top-right
      ctx.beginPath(); ctx.moveTo(W, 0); ctx.lineTo(W - lineLen, 0); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(W, 0); ctx.lineTo(W, lineLen); ctx.stroke();
      // Bottom-left
      ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(lineLen, H); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(0, H - lineLen); ctx.stroke();
      // Bottom-right
      ctx.beginPath(); ctx.moveTo(W, H); ctx.lineTo(W - lineLen, H); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(W, H); ctx.lineTo(W, H - lineLen); ctx.stroke();
    };

    draw();

    return () => {
      cancelAnimationFrame(frameRef.current);
      ro.disconnect();
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
});
